<script setup lang="ts">
import type { Form } from '#ui/types';
import { useMutation } from '@tanstack/vue-query';
import { z } from 'zod';

import {
    apiSendLoginOtp,
    apiVerifyLoginOtp,
    BackendErrorCode,
    type BackendErrorEntity,
    type BodyType,
    type ErrorType,
    type SendOtpBodyDto,
    type VerifyOtpBodyDto,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

interface LoginFormProps {
    email?: string;
}

// PROPS
const { email } = defineProps<LoginFormProps>();

// EVENTS
const emit = defineEmits<{
    success: [boolean];
    notFound: [string];
    notVerified: [string];
}>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { isLgOrGreater } = useResponsive();
const { state: authState, getSession } = useAuthUtils();
const { notifyBackendError, notifyError, notifyGenericError } = useNotification();
const { getToken, formatRequestOptions } = useInteractionOnlyTurnstile('login');
const { trackLoginSuccess } = useTracking();
const { resendCooldown, isResendDisabled, startResendCooldown } = useResendCooldown();

// MUTATIONS
const { mutateAsync: mutateSend } = useMutation<
    Awaited<ReturnType<typeof apiSendLoginOtp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<SendOtpBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiSendLoginOtp(data, formatRequestOptions(token)),
});

const { mutateAsync: mutateVerify } = useMutation<
    Awaited<ReturnType<typeof apiVerifyLoginOtp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<VerifyOtpBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiVerifyLoginOtp(data, formatRequestOptions(token)),
});

// DATA
const emailOnlySchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
});

const otpSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
    otp: z
        .array(z.string().length(1, tt('validation.msg.required', { field: tt('signup.verify.otp') })))
        .length(6, tt('validation.msg.invalid', { field: tt('signup.verify.otp') })),
});

type FormSchema = z.infer<typeof otpSchema>;

const isPromptingOtp = ref(false);
const isPendingSend = ref(false);
const isPendingVerify = ref(false);
const isSubmitting = computed(() => isPendingVerify.value || isPendingSend.value);
const formSchema = computed(() => (isPromptingOtp.value ? otpSchema : emailOnlySchema));

const formData = reactive<FormSchema>({
    email: email ?? '',
    otp: getEmptyOtp(),
});

const form = useTemplateRef<Form<FormSchema>>('form');

// COMPUTED
const submitLabel = computed(() => tt(isPromptingOtp.value ? 'login.loginButton' : 'login.nextButton'));

// METHODS
const sendOtp = async (): Promise<void> => {
    isPendingSend.value = true;
    let turnstileToken: string | null = null;

    try {
        try {
            turnstileToken = await getToken();
        } catch {
            notifyError({
                title: tt('validation.msg.captcha'),
                description: tt('notifications.tryAgain'),
            });

            return;
        }

        await mutateSend({ data: { email: formData.email }, token: turnstileToken }).then(() => {
            isPromptingOtp.value = true;
            formData.otp = getEmptyOtp();
            form.value?.clear();
            startResendCooldown();
        });
    } catch (apiError) {
        if (isFetchError(apiError) && isBackendError(apiError.data)) {
            if (apiError.data.error.code === BackendErrorCode.AuthNoUserByEmail) {
                emit('notFound', formData.email);

                return;
            }

            if (apiError.data.error.code === BackendErrorCode.AuthEmailNotVerified) {
                emit('notVerified', formData.email);

                return;
            }

            if (apiError.data.error.code === BackendErrorCode.OtpResendTooSoon) {
                startResendCooldown();
            }

            notifyBackendError({ error: apiError.data.error });

            return;
        }

        notifyGenericError();
    } finally {
        isPendingSend.value = false;
    }
};

const verifyOtp = async (): Promise<void> => {
    isPendingVerify.value = true;
    let turnstileToken: string | null = null;

    try {
        try {
            turnstileToken = await getToken();
        } catch {
            notifyError({
                title: tt('validation.msg.captcha'),
                description: tt('notifications.tryAgain'),
            });

            return;
        }

        await mutateVerify({ data: { email: formData.email, otp: formData.otp.join('') }, token: turnstileToken }).then(async ({ token }) => {
            authState.setToken(token);
            await getSession();
            emit('success', true);
            trackLoginSuccess();
        });
    } catch (apiError) {
        if (isFetchError(apiError) && isBackendError(apiError.data)) {
            if ([BackendErrorCode.OTPCodeNotValid, BackendErrorCode.OtpExpiredOrNotFound].includes(apiError.data.error.code)) {
                form.value?.setErrors([{ name: 'otp', message: tt('validation.msg.invalid', { field: tt('signup.verify.otp') }) }]);
            }

            notifyBackendError({ error: apiError.data.error });

            return;
        }

        notifyGenericError();
    } finally {
        isPendingVerify.value = false;
    }
};

const onResend = async (): Promise<void> => {
    let turnstileToken: string | null = null;

    try {
        turnstileToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.captcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    await mutateSend({ data: { email: formData.email }, token: turnstileToken })
        .then(() => {
            formData.otp = getEmptyOtp();
            form.value?.clear();
            startResendCooldown();
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            if (isFetchError(apiError) && isBackendError(apiError.data)) {
                if (apiError.data.error.code === BackendErrorCode.OtpResendTooSoon) {
                    startResendCooldown();
                }

                notifyBackendError({ error: apiError.data.error });

                return;
            }

            notifyGenericError();
        });
};

const onSubmit = async (): Promise<void> => {
    await (isPromptingOtp.value ? verifyOtp : sendOtp)();
};

// LIFECYCLE
onMounted(() => {
    if (email) sendOtp();
});
</script>

<template>
    <div class="flex flex-col gap-6">
        <UForm
            ref="form"
            class="flex flex-col justify-center gap-6"
            :state="formData"
            :schema="formSchema"
            :validate-on="['input', 'change']"
            @submit="onSubmit"
        >
            <p class="text-muted text-center font-medium">
                {{ isPromptingOtp ? tt('signup.verify.subtitle', { channel: tt('signup.verify.resend.email') }) : tt('login.subtitle') }}
            </p>

            <template v-if="!isPromptingOtp">
                <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput
                        v-model.trim="formData.email"
                        autocomplete="username"
                        type="email"
                        inputmode="email"
                        :disabled="isSubmitting"
                        :autofocus="isLgOrGreater"
                    />
                </UFormField>
            </template>

            <template v-else>
                <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput :model-value="formData.email" disabled :ui="{ base: 'disabled:bg-neutral-100' }">
                        <template #trailing>
                            <span class="text-primary cursor-pointer text-sm font-bold" @click="isPromptingOtp = false">{{
                                tt('login.change')
                            }}</span>
                        </template>
                    </UInput>
                </UFormField>

                <UFormField class="w-full" :label="tt('signup.verify.otp')" name="otp" required>
                    <UPinInput v-model="formData.otp" :disabled="isSubmitting" :autocomplete="AUTOCOMPLETE_OFF" :length="6" otp />
                </UFormField>
            </template>

            <TurnistileChallenge />

            <UButton block :label="submitLabel" type="submit" size="xl" :loading="isSubmitting" />

            <template v-if="isPromptingOtp">
                <UButton
                    color="neutral"
                    variant="outline"
                    :label="tt('signup.verify.resend.cta')"
                    size="xl"
                    block
                    :disabled="isResendDisabled || isPendingVerify"
                    :loading="isPendingSend"
                    @click="onResend"
                >
                    <template v-if="resendCooldown" #trailing>{{ resendCooldown }}</template>
                </UButton>
            </template>
        </UForm>
    </div>
</template>
